import React, { useEffect, useState, useRef, useMemo } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Upload, FileText, Image as ImageIcon, Trash2, Download, X, Maximize2, Calendar, GitCompare, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const isImage = (ct) => ct?.startsWith("image/");
const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/1024/1024).toFixed(2)} MB`;

// Albanian day names
const DAY_SQ = ["E Diel", "E Hene", "E Marte", "E Merkure", "E Enjte", "E Premte", "E Shtune"];
const MONTH_SQ = ["Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor", "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor"];

const fmtDayHeader = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (isSameDay(d, today)) return "Sot";
  if (isSameDay(d, yesterday)) return "Dje";
  return `${DAY_SQ[d.getDay()]}, ${d.getDate()} ${MONTH_SQ[d.getMonth()]} ${d.getFullYear()}`;
};

const fmtTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString("sq-AL", { hour: "2-digit", minute: "2-digit", hour12: false });
};

const dayKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
};

/**
 * Loads the file's actual binary via the authenticated /files/{id}/download
 * endpoint and shows a real thumbnail for images. Falls back to a generic
 * document icon for PDFs (or when the fetch fails).
 * The blob URL is created on mount and revoked on unmount so we do not leak.
 */
function FileThumb({ file }) {
  const [src, setSrc] = useState(null);
  const [errored, setErrored] = useState(false);
  const image = isImage(file.content_type);

  useEffect(() => {
    if (!image) return;
    let alive = true;
    let blobUrl = null;
    api.get(`/files/${file.id}/download`, { responseType: "blob" })
      .then((resp) => {
        if (!alive) return;
        blobUrl = URL.createObjectURL(resp.data);
        setSrc(blobUrl);
      })
      .catch(() => { if (alive) setErrored(true); });
    return () => {
      alive = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [file.id, image]);

  if (!image) {
    return <FileText size={40} className="text-[#5C5C5C]" />;
  }
  if (errored) {
    return <ImageIcon size={40} className="text-[#5C5C5C]" />;
  }
  if (!src) {
    // Skeleton placeholder while blob loads
    return <div className="h-full w-full animate-pulse bg-[#EAE4D9]" aria-hidden />;
  }
  return (
    <img
      src={src}
      alt={file.filename}
      loading="lazy"
      className="h-full w-full object-contain bg-black/5"
      data-testid={`file-thumb-${file.id}`}
      draggable={false}
    />
  );
}

export default function PatientFiles({ patientId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // { url, name }
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]); // array of file objects (max 2)
  const [compareView, setCompareView] = useState(null); // { left, right }
  const inputRef = useRef();

  // Group files by upload day, keep newest-first ordering (already sorted by API)
  const groupedByDay = useMemo(() => {
    const groups = new Map();
    for (const f of files) {
      if (!f.created_at) continue;
      const key = dayKey(f.created_at);
      if (!groups.has(key)) groups.set(key, { key, iso: f.created_at, items: [] });
      groups.get(key).items.push(f);
    }
    // Sort groups by day DESC (newest first). Items inside each group are already newest-first.
    return Array.from(groups.values()).sort((a, b) => (a.key < b.key ? 1 : -1));
  }, [files]);

  const load = async () => {
    const { data } = await api.get(`/patients/${patientId}/files`);
    setFiles(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

  const onSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/patients/${patientId}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Skedari u ngarkua");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gabim gjatë ngarkimit");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Fshini këtë skedar?")) return;
    try {
      await api.delete(`/files/${id}`);
      toast.success("U fshi");
      load();
    } catch {
      toast.error("Gabim");
    }
  };

  const openPreview = async (f) => {
    try {
      const resp = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setPreview({ url, name: f.filename, type: f.content_type });
    } catch { toast.error("Nuk u hap dot"); }
  };

  // Close preview on ESC
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => {
      if (e.key === "Escape") closePreview();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while previewing
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [preview]);

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const download = async (f) => {
    try {
      const resp = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gabim gjatë shkarkimit"); }
  };

  // Compare mode helpers
  const imageFiles = useMemo(() => files.filter((f) => isImage(f.content_type)), [files]);

  const toggleCompareMode = () => {
    setCompareMode((v) => !v);
    setSelected([]);
  };

  const toggleSelect = (file) => {
    setSelected((prev) => {
      if (prev.find((x) => x.id === file.id)) {
        return prev.filter((x) => x.id !== file.id);
      }
      if (prev.length >= 2) {
        // Rotate — drop the oldest selection
        return [prev[1], file];
      }
      return [...prev, file];
    });
  };

  const openCompare = async () => {
    if (selected.length !== 2) {
      toast.error("Zgjidh saktesisht 2 imazhe per te krahasuar");
      return;
    }
    try {
      const [a, b] = selected;
      const [rA, rB] = await Promise.all([
        api.get(`/files/${a.id}/download`, { responseType: "blob" }),
        api.get(`/files/${b.id}/download`, { responseType: "blob" }),
      ]);
      setCompareView({
        left: { url: URL.createObjectURL(rA.data), file: a },
        right: { url: URL.createObjectURL(rB.data), file: b },
      });
    } catch {
      toast.error("Nuk u ngarkuan dot imazhet");
    }
  };

  const closeCompare = () => {
    if (compareView) {
      URL.revokeObjectURL(compareView.left.url);
      URL.revokeObjectURL(compareView.right.url);
    }
    setCompareView(null);
  };

  // Lock body scroll while comparing / previewing + ESC to close
  useEffect(() => {
    if (!compareView) return;
    const onKey = (e) => { if (e.key === "Escape") closeCompare(); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [compareView]);

  return (
    <div className="surface-card p-6" data-testid="patient-files">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Arkivi</div>
          <h3 className="mt-1 text-2xl font-semibold tracking-tight">Skedarë & Rëntgen</h3>
          <p className="mt-1 text-sm text-[#5C5C5C]">Ngarkoni imazhe (jpg/png/webp/gif/jfif) ose PDF, deri 10MB.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {compareMode && (
            <>
              <span className="text-xs text-[#5C5C5C]" data-testid="compare-selected-count">
                {selected.length}/2 zgjedhur
              </span>
              <button onClick={openCompare}
                      disabled={selected.length !== 2}
                      className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="compare-open-btn">
                <GitCompare size={16} /> Krahaso
              </button>
              <button onClick={toggleCompareMode}
                      className="btn-ghost"
                      data-testid="compare-exit-btn">
                Anulo
              </button>
            </>
          )}
          {!compareMode && imageFiles.length >= 2 && (
            <button onClick={toggleCompareMode}
                    className="btn-ghost"
                    data-testid="compare-mode-btn">
              <GitCompare size={16} /> Krahaso rëntgenet
            </button>
          )}
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,.jfif,application/pdf"
                 onChange={onSelect} className="hidden" data-testid="file-input" />
          <button onClick={() => inputRef.current?.click()} disabled={uploading} className="btn-primary" data-testid="upload-file-button">
            <Upload size={16} /> {uploading ? "Duke ngarkuar…" : "Ngarko Skedar"}
          </button>
        </div>
      </div>

      {compareMode && (
        <div className="mb-4 rounded-lg border border-[#D4A24C] bg-[#D4A24C]/10 px-4 py-3 text-sm text-[#8B6914]"
             data-testid="compare-hint">
          Klikoni 2 imazhe (rëntgen ose foto) për të krahasuar side-by-side. PDF-t nuk mbështeten në modalitetin e krahasimit.
        </div>
      )}

      {files.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#E2DFD8] py-12 text-center text-sm text-[#5C5C5C]">
          Pa skedarë ende. Klikoni "Ngarko Skedar" për të filluar.
        </div>
      ) : (
        <div className="space-y-8" data-testid="files-timeline">
          {groupedByDay.map((group) => (
            <section key={group.key} data-testid={`files-day-${group.key}`}>
              <div className="flex items-baseline gap-3 mb-3 pb-2 border-b border-[#E2DFD8]">
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-[#2C4C3B]" />
                  <h4 className="text-sm font-semibold tracking-tight text-[#1A1A1A]">
                    {fmtDayHeader(group.iso)}
                  </h4>
                </div>
                <span className="text-xs text-[#5C5C5C]">
                  {group.items.length} skedar{group.items.length !== 1 ? "ë" : ""}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.items.map((f) => {
                  const isSelected = !!selected.find((x) => x.id === f.id);
                  const canCompare = compareMode && isImage(f.content_type);
                  const cardOnClick = () => {
                    if (canCompare) toggleSelect(f);
                    else openPreview(f);
                  };
                  return (
                    <div key={f.id}
                         className={`group rounded-xl border overflow-hidden bg-white transition-all ${
                           isSelected
                             ? "border-[#2C4C3B] ring-2 ring-[#2C4C3B]/40 shadow-md"
                             : compareMode && !isImage(f.content_type)
                               ? "border-[#E2DFD8] opacity-40"
                               : "border-[#E2DFD8] hover:border-[#2C4C3B]/40"
                         }`}
                         data-testid={`file-${f.id}`}>
                      <button onClick={cardOnClick}
                              disabled={compareMode && !isImage(f.content_type)}
                              className="relative block w-full aspect-video bg-[#FDFBF7] flex items-center justify-center overflow-hidden hover:opacity-90 transition-opacity disabled:cursor-not-allowed">
                        <FileThumb file={f} />
                        {canCompare && (
                          <span className={`absolute top-2 left-2 flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                            isSelected
                              ? "bg-[#2C4C3B] border-[#2C4C3B] text-white"
                              : "bg-white/90 border-[#5C5C5C] text-transparent"
                          }`}>
                            {isSelected ? <CheckCircle2 size={16} /> : ""}
                          </span>
                        )}
                      </button>
                      <div className="p-3">
                        <div className="text-sm font-medium truncate" title={f.filename}>{f.filename}</div>
                        <div className="text-xs text-[#5C5C5C] mt-0.5 flex items-center gap-1.5 flex-wrap">
                          <span data-testid={`file-time-${f.id}`}>{fmtTime(f.created_at)}</span>
                          <span aria-hidden>·</span>
                          <span>{fmtSize(f.size)}</span>
                          <span aria-hidden>·</span>
                          <span className="truncate">{f.uploaded_by}</span>
                        </div>
                        {!compareMode && (
                          <div className="mt-3 flex items-center justify-end gap-1">
                            <button onClick={() => download(f)}
                                    className="p-1.5 rounded hover:bg-[#EAE4D9]"
                                    title="Shkarko"
                                    data-testid={`download-${f.id}`}>
                              <Download size={14} />
                            </button>
                            <button onClick={() => remove(f.id)}
                                    className="p-1.5 rounded hover:bg-[#9E3C3C]/10 text-[#9E3C3C]"
                                    title="Fshi"
                                    data-testid={`delete-file-${f.id}`}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-[100] flex flex-col bg-black/95"
          onClick={closePreview}
          data-testid="file-preview-modal"
          role="dialog"
          aria-label={`Parashikim: ${preview.name}`}
        >
          {/* Top toolbar */}
          <div
            className="flex items-center justify-between gap-4 px-4 py-3 text-white/90"
            style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium" title={preview.name}>{preview.name}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-white/50">
                {isImage(preview.type) ? "Imazh" : "Dokument PDF"}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <a
                href={preview.url}
                download={preview.name}
                className="inline-flex items-center gap-1.5 rounded-md bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs"
                title="Shkarko"
                data-testid="preview-download-btn"
              >
                <Download size={14} /> <span className="hidden sm:inline">Shkarko</span>
              </a>
              {isImage(preview.type) && (
                <button
                  onClick={() => window.open(preview.url, "_blank", "noopener,noreferrer")}
                  className="inline-flex items-center gap-1.5 rounded-md bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs"
                  title="Hape ne tab te ri"
                  data-testid="preview-open-tab-btn"
                >
                  <Maximize2 size={14} /> <span className="hidden sm:inline">Tab i ri</span>
                </button>
              )}
              <button
                onClick={closePreview}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/25 transition-colors"
                title="Mbyll (ESC)"
                aria-label="Mbyll"
                data-testid="preview-close-btn"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Content — full remaining height */}
          <div
            className="flex-1 min-h-0 flex items-center justify-center overflow-hidden p-2 sm:p-4"
            onClick={(e) => e.stopPropagation()}
          >
            {isImage(preview.type) ? (
              <img
                src={preview.url}
                alt={preview.name}
                className="max-h-full max-w-full object-contain rounded-lg shadow-2xl select-none"
                draggable={false}
                data-testid="preview-image"
              />
            ) : (
              <iframe
                src={preview.url}
                title={preview.name}
                className="h-full w-full max-w-6xl rounded-lg bg-white"
                data-testid="preview-iframe"
              />
            )}
          </div>

          {/* Bottom hint */}
          <div className="hidden sm:block px-4 py-2 text-center text-[11px] text-white/40"
               onClick={(e) => e.stopPropagation()}>
            Kliko jashte ose shtyp ESC per te mbyllur
          </div>
        </div>
      )}

      {compareView && (
        <div
          className="fixed inset-0 z-[100] flex flex-col bg-black/95"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeCompare(); }}
          data-testid="file-compare-modal"
          role="dialog"
          aria-label="Krahaso rëntgenet"
        >
          {/* Toolbar */}
          <div
            className="flex items-center justify-between gap-4 px-4 py-3 text-white/90"
            style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">Krahaso rëntgenet</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-white/50">
                Shtyp ESC ose X për të mbyllur
              </div>
            </div>
            <button
              onClick={closeCompare}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/25 transition-colors"
              title="Mbyll (ESC)"
              aria-label="Mbyll"
              data-testid="compare-close-btn"
            >
              <X size={20} />
            </button>
          </div>

          {/* Two panels side-by-side on desktop, stacked on mobile */}
          <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 gap-1 p-1">
            {[compareView.left, compareView.right].map((side, idx) => (
              <div key={idx}
                   className="relative flex flex-col rounded-md overflow-hidden bg-black/70 border border-white/10"
                   data-testid={`compare-panel-${idx === 0 ? "left" : "right"}`}>
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-black/60 text-white/80 text-xs">
                  <div className="min-w-0 flex-1 truncate" title={side.file.filename}>
                    <span className="font-medium">{side.file.filename}</span>
                  </div>
                  <div className="shrink-0 text-white/50 text-[10px]">
                    {new Date(side.file.created_at).toLocaleDateString("sq-AL", {
                      day: "2-digit", month: "short", year: "numeric",
                    })}
                  </div>
                  <a href={side.url} download={side.file.filename}
                     className="p-1 rounded hover:bg-white/10"
                     title="Shkarko">
                    <Download size={14} />
                  </a>
                </div>
                <div className="flex-1 min-h-0 flex items-center justify-center overflow-hidden p-2">
                  <img
                    src={side.url}
                    alt={side.file.filename}
                    className="max-h-full max-w-full object-contain select-none"
                    draggable={false}
                    data-testid={`compare-img-${idx === 0 ? "left" : "right"}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
